import { useRef, useState } from "react";
import { GripVertical, Plus, Trash2, Upload, Link2, X } from "lucide-react";
import { adminApi } from "../api";
import { resolveImage } from "../../lib/data";
import { toast } from "sonner";

/**
 * Gallery editor — manages an array of image URLs.
 * Supports: add via URL, upload from file, drag-reorder (via up/down arrows), remove.
 */
export default function GalleryEditor({ value = [], onChange, testId = "gallery-editor" }) {
  const images = Array.isArray(value) ? value : [];
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const commit = (next) => onChange(next);

  const addUrl = () => {
    if (!url.trim()) return;
    commit([...images, url.trim()]);
    setUrl("");
    setAdding(false);
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await adminApi.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.url);
      }
      commit([...images, ...uploaded]);
      toast.success(`${uploaded.length} immagine/i aggiunta/e`);
    } catch (err) {
      toast.error("Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAt = (i) => commit(images.filter((_, j) => j !== i));

  const move = (i, delta) => {
    const next = [...images];
    const target = i + delta;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    commit(next);
  };

  return (
    <div data-testid={testId} className="space-y-3">
      {images.length > 0 && (
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {images.map((img, i) => (
            <li
              key={`${img}-${i}`}
              className="group relative rounded-lg overflow-hidden border border-white/10 bg-[#08080A]"
              data-testid={`${testId}-item-${i}`}
            >
              <div className="aspect-[4/3] relative">
                <img
                  src={resolveImage(img)}
                  alt={`Galleria ${i + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => (e.currentTarget.style.opacity = "0.3")}
                />
              </div>
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="h-8 px-2 rounded-full bg-white/90 text-black text-xs disabled:opacity-30"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === images.length - 1}
                  className="h-8 px-2 rounded-full bg-white/90 text-black text-xs disabled:opacity-30"
                >
                  →
                </button>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  data-testid={`${testId}-remove-${i}`}
                  className="h-8 w-8 rounded-full bg-red-500 text-white grid place-items-center"
                  aria-label="Rimuovi"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
                {i + 1}
              </span>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="flex gap-2 items-center">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            autoFocus
            className="flex-1 bg-transparent border-b border-white/15 focus:border-[#C48F56] outline-none py-2 text-sm"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())}
          />
          <button
            type="button"
            onClick={addUrl}
            className="rounded-full bg-white text-black px-4 py-1.5 text-xs"
          >
            Aggiungi
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setUrl("");
            }}
            className="h-8 w-8 rounded-full border border-white/10 grid place-items-center"
          >
            <X size={13} />
          </button>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={uploadFiles}
            className="hidden"
            data-testid={`${testId}-upload`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs hover:bg-white/5 disabled:opacity-50"
          >
            <Upload size={12} /> {uploading ? "Caricamento…" : "Carica immagini"}
          </button>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs hover:bg-white/5"
          >
            <Link2 size={12} /> URL esterno
          </button>
          <span className="inline-flex items-center text-[11px] text-white/40 font-mono uppercase tracking-[0.2em] ml-auto">
            {images.length} / 20
          </span>
        </div>
      )}
    </div>
  );
}
